import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { Image, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { AppView, Button, CardView, NavigationHeader, Spacer } from 'design-system';
import useStyles from 'hooks/useStyles';
import Amplitude from 'utils/amplitude';
import { useAppSelector } from 'store/hooks';
import { getBalance } from 'utils/api/getBalance';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { formatAmount, formatSortCode } from 'utils/formatting';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { selectConnectedBankAccount } from 'features/wealth/selectors';
import LogoBankAccount from 'design-system/LogoBankAccount';
import Modal from 'utils/packages/Modal';
import useColors from 'hooks/useColors';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { OnboardingFlow } from 'utils/types/navigation';
import { useConnectedAccount } from '../hooks/selectors';
import { isWeb } from '../../../constants';
const directDebitLogo = require('features/invest/img/directDebitLogo.png');
const AutoinvestSelectFundingAccountScreen = ({ route, navigation, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const mountedOnAnotherScreen = 'mountedOnAnotherScreen' in route.params ? route.params?.mountedOnAnotherScreen : undefined;
    const bankAccount = 'account' in route.params ? route.params.account : undefined;
    const connectedAccount = useConnectedAccount();
    const connectedBankAccount = useAppSelector(selectConnectedBankAccount);
    const scrollY = useSharedValue(0);
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const linkedAccount = useMemo(() => {
        if (bankAccount) {
            return bankAccount;
        }
        if (connectedBankAccount) {
            return connectedBankAccount;
        }
        if (connectedAccount) {
            return connectedAccount;
        }
        return undefined;
    }, [bankAccount, connectedAccount, connectedBankAccount]);
    const [primaryBalance] = linkedAccount ? getBalance(linkedAccount) : [undefined];
    const hasLinkedAccount = !!linkedAccount;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            Amplitude.logEvent('Confirm Direct Debit Account', { type: 'autoinvest' });
        }
    }, [mountedOnAnotherScreen]);
    const handleContinue = useCallback(() => {
        if (linkedAccount && !('ukSortCode' in linkedAccount) && linkedAccount?.originalType === 'SAVINGS') {
            Modal.showConfirmation({
                title: 'Direct Debit isn’t available',
                subtitle: 'Your funding account is a savings account, which doesn‘t support Direct Debit. To change your funding account, please get in touch with our support team.',
                confirmButtonTitle: 'Okay',
                cancelButtonTitle: 'Contact support',
                onPressCancel: () => {
                    navigation.navigate('Support');
                },
            });
            return;
        }
        if (linkedAccount) {
            if ('onDirectDebitSetup' in route.params) {
                navigation.navigate('AutoinvestConfirmBankDetails', {
                    onDirectDebitSetup: route.params.onDirectDebitSetup,
                });
            }
            else {
                navigation.navigate('AutoinvestConfirmBankDetails', {
                    accountType: route.params.accountType,
                    collectionDate: route.params.collectionDate,
                    amount: route.params.amount,
                    allocations: route.params.allocations,
                    planId: route.params.planId,
                    account: linkedAccount,
                });
            }
        }
    }, [linkedAccount, route.params]);
    const formattedSortCode = useMemo(() => {
        if (!linkedAccount)
            return '';
        if ('sortCode' in linkedAccount) {
            return formatSortCode(linkedAccount.sortCode);
        }
        if ('ukSortCode' in linkedAccount) {
            return formatSortCode(linkedAccount.ukSortCode);
        }
        return '';
    }, [linkedAccount]);
    const accountNumber = useMemo(() => {
        if (!linkedAccount)
            return '';
        if ('accountNumber' in linkedAccount) {
            return linkedAccount.accountNumber;
        }
        return linkedAccount.ukAccountNumber;
    }, [linkedAccount]);
    const accountName = useMemo(() => {
        if (linkedAccount) {
            if ('accountNumber' in linkedAccount) {
                return linkedAccount.name;
            }
            return linkedAccount.bankName || 'Linked bank';
        }
        return 'Linked bank';
    }, [linkedAccount]);
    const isContinueDisabled = !linkedAccount;
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Invest}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && styles.rightBtnContainer}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title="Set up your Direct Debit" subTitle="We’ll use this account to collect payments via Direct Debit"/>

        {!!hasLinkedAccount && (<CardView noMarginTop>
            <View style={styles.fundingAccountCard}>
              <View style={styles.row}>
                <View style={styles.iconContainer}>
                  <LogoBankAccount size="R44" onCard data={linkedAccount}/>
                </View>
                <Spacer w={16}/>
                <View style={styles.flex}>
                  <Text Text-12 Primary>
                    FUNDING ACCOUNT
                  </Text>
                  <Text Text-16 numberOfLines={1}>
                    {accountName}
                  </Text>
                  <Text TextThin-14 Secondary>
                    {formattedSortCode} {accountNumber}
                  </Text>
                </View>
                {primaryBalance ? (<Text Numbers-16>{formatAmount(primaryBalance.amount, primaryBalance.currency, 2)}</Text>) : null}
              </View>
            </View>
          </CardView>)}

        <CardView withPadding noMarginTop>
          <Text TextThin-14 Secondary>
            Instruction to your Bank or Building Society. Please pay Seccl Custody Ltd Direct Debits from the account
            detailed in this Instruction subject to the safeguards assured by the Direct Debit Guarantee. I understand
            that this instruction may remain with Seccl Custody Ltd and, if so, details will be passed electronically to
            my bank/building society.
          </Text>
          <Spacer.H16 />
          <Text TextThin-14 Secondary>
            Banks and building societies may not accept Direct Debit Instructions for some types of account.
          </Text>
          <Spacer.H8 />
          <View style={styles.ddGuaranteeRow}>
            <View style={styles.flex}>
              <Text TextThin-14 Secondary>
                The Direct Debit Guarantee
              </Text>
            </View>
            <Spacer.W16 />
            <Image source={directDebitLogo} style={styles.ddLogo}/>
          </View>
          <Spacer.H8 />
          <Text TextThin-14 Secondary>
            This Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits.
          </Text>
          <Spacer.H16 />
          <Text TextThin-14 Secondary>
            If there are any changes to the amount, date or frequency of your Direct Debit, Seccl Custody will notify
            you 5 working days in advance of your account being debited or as otherwise agreed. If you request Seccl
            Technology to collect a payment, confirmation of the amount and date will be given to you at the time of the
            request.
          </Text>
          <Spacer.H16 />
          <Text TextThin-14 Secondary>
            If an error is made in the payment of your Direct Debit, by Seccl Technology or your bank or building
            society, you are entitled to a full and immediate refund of the amount paid from your bank or building
            society. If you receive a refund you are not entitled to, you must pay it back when Seccl Technology asks
            you to.
          </Text>
          <Spacer.H16 />
          <Text TextThin-14 Secondary>
            You can cancel a Direct Debit at any time by simply contacting your bank or building society. Written
            confirmation may be required. Please also notify us.
          </Text>
        </CardView>
      </Animated.ScrollView>
      <Button brand floating title="Continue" onPress={handleContinue} disabled={isContinueDisabled} isInTab={!!mountedOnAnotherScreen}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    fundingAccountCard: {
        padding: rem(16),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    iconContainer: {
        width: rem(44),
        height: rem(44),
        alignItems: 'center',
        borderRadius: rem(22),
        justifyContent: 'center',
        borderWidth: rem(1),
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.contrast,
    },
    flex: {
        flex: 1,
    },
    horizontalPadding: {
        paddingHorizontal: rem(16),
    },
    disabledRow: {
        opacity: 0.5,
    },
    ddGuaranteeRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    ddLogo: {
        width: rem(80),
        height: rem(40),
        tintColor: colors.text.primary,
    },
    rightBtnContainer: {
        marginRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
}));
export default AutoinvestSelectFundingAccountScreen;
